import {PopoverPrimitive as Popover} from "ch3-ui-lib";
import {PhoneHQButton, PhoneHQConfirmDialog, PhoneHQModal, PhoneHQSelect} from "ch3-ui-lib";
import EmojiPicker, {EmojiStyle} from "emoji-picker-react";
import {Smile, X} from "lucide-react";
import moment from "moment-timezone";
import {useEffect, useState} from "react";
import {useChangeStatusMutation} from "../../store/features/Team/teamService";
import {showToast} from "../../store/features/Notifications/notificationsSlice";
import {useAppDispatch} from "../../store/store";
import {DateTimeFields} from "../DateTimeFields/DateTimeFields";
import {nextQuarterHour} from "../Dnd/dndTime";
import {hexToEmoji} from "../Emoji/emoji";
import {CLEAR_OPTIONS, type ClearAfter, clearLabel, type CustomStatus, isStatusActive, NAME_MAX, statusDueDate, SUGGESTIONS} from "./statusModel";

const lucide = '!p-0 shrink-0 !text-current';
const PAYLOAD_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";

interface SetStatusDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userId: string | number | null | undefined;
    current: CustomStatus | null | undefined;
}

/** SettingsStatus-Set / -Emoji / -Clear / -Date / -ClearDialog / -Done. */
export const SetStatusDialog = ({open, onOpenChange, userId, current}: SetStatusDialogProps) => {
    const dispatch = useAppDispatch();
    const [changeStatus, {isLoading}] = useChangeStatusMutation();
    const [name, setName] = useState('');
    const [icon, setIcon] = useState('');
    const [clearAfter, setClearAfter] = useState<ClearAfter>('today');
    const [customAt, setCustomAt] = useState(() => nextQuarterHour(new Date()));
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    const active = isStatusActive(current);

    // Each opening starts from the status people see now.
    useEffect(() => {
        if (!open) return;
        setName(active ? current?.name ?? '' : '');
        setIcon(active ? current?.icon ?? '' : '');
        if (active && current?.dueDate) {
            setClearAfter('custom');
            setCustomAt(new Date(current.dueDate));
        } else {
            setClearAfter(active ? 'never' : 'today');
            setCustomAt(nextQuarterHour(new Date()));
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const customInPast = clearAfter === 'custom' && customAt <= new Date();
    const canSave = Boolean(icon && name.trim()) && !customInPast && !isLoading;

    const send = async (data: { name: string; icon: string; dueDate: string | null }, toast: string) => {
        try {
            await changeStatus({data, userId: userId == null ? null : String(userId)}).unwrap();
            dispatch(showToast({description: toast, type: 'success'}));
            onOpenChange(false);
        } catch {
            dispatch(showToast({description: 'Couldn’t update your status. Try again.', type: 'error'}));
        }
    };

    const save = () => {
        const due = statusDueDate(clearAfter, new Date(), customAt);
        void send({name: name.trim(), icon, dueDate: due ? moment(due).format(PAYLOAD_FORMAT) : null}, 'Status updated.');
    };

    const clear = () => {
        setConfirmClear(false);
        void send({name: '', icon: '', dueDate: ''}, 'Status cleared.');
    };

    return (
        <>
            <PhoneHQModal
                open={open}
                onOpenChange={onOpenChange}
                title="Set status"
                width={480}
                footerStart={active && (
                    <button type="button" onClick={() => setConfirmClear(true)}
                            className="cursor-pointer border-0 bg-transparent p-0 text-callout font-semibold text-danger-ink hover:underline">
                        Clear status
                    </button>
                )}
                footer={
                    <>
                        <PhoneHQButton variant="secondary" className="px-5" onClick={() => onOpenChange(false)}>Cancel</PhoneHQButton>
                        <PhoneHQButton className="px-5" disabled={!canSave} onClick={save}>Save</PhoneHQButton>
                    </>
                }
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="status-name" className="text-footnote font-medium text-ink-secondary">Your status</label>
                        <div className="flex items-center gap-2">
                            <Popover.Root open={emojiOpen} onOpenChange={setEmojiOpen}>
                                <Popover.Trigger asChild>
                                    <button type="button" aria-label="Choose emoji"
                                            className="box-border flex size-9 flex-none cursor-pointer items-center justify-center rounded-control border border-solid border-fill-subtle bg-surface p-0 text-ink-muted hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                                        {icon ? hexToEmoji(icon, '!w-5 !h-5') : <Smile size={18} aria-hidden="true" className={lucide}/>}
                                    </button>
                                </Popover.Trigger>
                                <Popover.Portal>
                                    <Popover.Content align="start" sideOffset={6} className="z-[60] overflow-hidden rounded-xl border border-solid border-line bg-surface shadow-overlay outline-none" aria-label="Choose emoji">
                                        <EmojiPicker
                                            emojiStyle={EmojiStyle.TWITTER}
                                            width={340}
                                            height={360}
                                            previewConfig={{showPreview: false}}
                                            skinTonesDisabled
                                            lazyLoadEmojis
                                            onEmojiClick={(e) => {
                                                setIcon(e.unified);
                                                setEmojiOpen(false);
                                            }}
                                        />
                                    </Popover.Content>
                                </Popover.Portal>
                            </Popover.Root>
                            <span className="relative flex min-w-0 flex-1 items-center">
                                <input
                                    id="status-name"
                                    value={name}
                                    maxLength={NAME_MAX}
                                    placeholder="What’s your status?"
                                    onChange={(e) => setName(e.target.value)}
                                    className="box-border h-control-md w-full rounded-control border border-solid border-fill-subtle bg-surface px-3 pr-9 text-callout text-ink outline-none placeholder:text-ink-muted focus:border-transparent focus:ring-2 focus:ring-brand-focus"
                                />
                                {name && (
                                    <button type="button" aria-label="Clear text" onClick={() => setName('')}
                                            className="absolute right-1 flex size-7 cursor-pointer items-center justify-center rounded-chip border-0 bg-transparent p-0 text-ink-muted hover:bg-surface-muted">
                                        <X size={14} aria-hidden="true" className={lucide}/>
                                    </button>
                                )}
                            </span>
                        </div>
                    </div>

                    <PhoneHQSelect<ClearAfter>
                        label="Clear after"
                        value={clearAfter}
                        onChange={setClearAfter}
                        options={CLEAR_OPTIONS}
                    />
                    {clearAfter === 'custom' && (
                        <div className="-mt-1">
                            <DateTimeFields value={customAt} onChange={setCustomAt} now={new Date()}/>
                            {customInPast && <p role="alert" className="m-0 mt-2 text-footnote text-danger-ink">Choose a time in the future.</p>}
                        </div>
                    )}

                    <div className="flex flex-col gap-0.5">
                        <h3 className="m-0 mb-1 text-caption text-ink-muted">Suggestions</h3>
                        {SUGGESTIONS.map((s) => (
                            <button
                                key={s.name}
                                type="button"
                                onClick={() => {
                                    setName(s.name);
                                    setIcon(s.icon);
                                    setClearAfter(s.clearAfter);
                                }}
                                className="box-border flex h-10 w-full cursor-pointer items-center gap-3 rounded-chip border-0 bg-transparent px-3 text-left hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
                            >
                                <span className="flex w-6 justify-center">{hexToEmoji(s.icon, '!w-5 !h-5')}</span>
                                <span className="flex-1 text-callout text-ink">{s.name}</span>
                                <span className="text-footnote text-ink-muted">{clearLabel(s.clearAfter)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </PhoneHQModal>

            <PhoneHQConfirmDialog
                open={confirmClear}
                onOpenChange={setConfirmClear}
                title="Clear status?"
                cancelLabel="Cancel"
                confirmLabel="Clear status"
                tone="danger"
                onConfirm={clear}
            >
                Your status “{current?.name}” will be removed and your team will no longer see it.
            </PhoneHQConfirmDialog>
        </>
    );
};
